import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import Badge from '../../../components/ui/Badge';
import {
  CheckCircleIcon,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  PencilIcon,
  PlusIcon,
  Select,
  TrashIcon,
  useConfirm,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import { deletePmsMaintenanceCounter } from '../../../services/mutations';
import { normalizePaginatedListResponse } from '../../../services/paginatedList';
import type { PmsMaintenanceCounter } from './useData';

type BoardTab = 'active' | 'inactive' | 'all';
type LookupRow = { id: number; code?: string | null; name?: string | null };

const viewTabs: Array<{
  id: BoardTab;
  label: string;
  icon: React.ReactNode;
  emptyTitle: string;
  emptyHint: string;
}> = [
  {
    id: 'active',
    label: 'Active',
    icon: <CheckCircleIcon className="h-4 w-4" />,
    emptyTitle: 'No active counters',
    emptyHint: 'Active running-hour and usage counters appear here.',
  },
  {
    id: 'inactive',
    label: 'Inactive',
    icon: <FolderIcon className="h-4 w-4" />,
    emptyTitle: 'No inactive counters',
    emptyHint: 'Deactivated counters appear here.',
  },
  {
    id: 'all',
    label: 'All',
    icon: <DocumentTextIcon className="h-4 w-4" />,
    emptyTitle: 'No counters yet',
    emptyHint: 'Create counters to track equipment usage for PMS schedules.',
  },
];

function listFrom<T>(rows: T[] | { data?: T[] } | undefined): T[] {
  return Array.isArray(rows) ? rows : rows?.data ?? [];
}

function vesselLabel(row: PmsMaintenanceCounter) {
  const v = row.vessel as { vesselName?: string; name?: string } | null | undefined;
  return v?.vesselName ?? v?.name ?? (row.vesselId != null ? `#${row.vesselId}` : '');
}

function typeLabel(row: PmsMaintenanceCounter) {
  const t = row.counterType as { name?: string; code?: string } | null | undefined;
  return t?.name ?? t?.code ?? (row.counterTypeId != null ? `#${row.counterTypeId}` : '');
}

function sourceLabel(row: PmsMaintenanceCounter) {
  const s = row.source as { name?: string; code?: string } | null | undefined;
  return s?.name ?? s?.code ?? (row.sourceId != null ? `#${row.sourceId}` : '');
}

function valueLabel(row: PmsMaintenanceCounter) {
  if (row.currentValue == null) return '';
  const unit = row.unit != null && row.unit !== '' ? ` ${String(row.unit)}` : '';
  return `${row.currentValue}${unit}`;
}

function isActive(row: PmsMaintenanceCounter) {
  return row.isActive !== false;
}

function sortValue(row: PmsMaintenanceCounter, key: string): string | number {
  switch (key) {
    case 'name':
      return String(row.name ?? '').toLowerCase();
    case 'vessel':
      return vesselLabel(row).toLowerCase();
    case 'type':
      return typeLabel(row).toLowerCase();
    case 'source':
      return sourceLabel(row).toLowerCase();
    case 'currentValue':
      return row.currentValue != null ? Number(row.currentValue) : Number.NEGATIVE_INFINITY;
    case 'mode':
      return row.isAutomatic ? 1 : 0;
    case 'createdAt':
      return row.createdAt ? String(row.createdAt) : '';
    default:
      return '';
  }
}

const List: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const returnState = { from: `${location.pathname}${location.search}` };
  const confirm = useConfirm();

  const [tab, setTab] = useState<BoardTab>('active');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [vesselId, setVesselId] = useState('');
  const [counterTypeId, setCounterTypeId] = useState('');
  const [sourceId, setSourceId] = useState('');

  type VesselOptionRow = { id: number; vesselName: string; isActive?: boolean };
  const { data: vesselsRaw, isLoading: vesselsLoading } = useSWR<
    VesselOptionRow[] | { data?: VesselOptionRow[] }
  >('/vessels?page=1&limit=500', fetcher, { revalidateOnFocus: false });
  const { data: typesRaw } = useSWR<LookupRow[] | { data?: LookupRow[] }>(
    '/pms-counter-types?page=1&limit=500',
    fetcher,
    { revalidateOnFocus: false },
  );
  const { data: sourcesRaw } = useSWR<LookupRow[] | { data?: LookupRow[] }>(
    '/pms-counter-sources?page=1&limit=500',
    fetcher,
    { revalidateOnFocus: false },
  );

  const vesselOptions = useMemo(() => {
    const list = listFrom(vesselsRaw).filter((v) => v.isActive !== false);
    return list.map((v) => ({ value: String(v.id), label: v.vesselName }));
  }, [vesselsRaw]);

  const typeOptions = useMemo(() => {
    const list = listFrom(typesRaw);
    return list.map((t) => ({ value: String(t.id), label: t.name ?? t.code ?? `#${t.id}` }));
  }, [typesRaw]);

  const sourceOptions = useMemo(() => {
    const list = listFrom(sourcesRaw);
    return list.map((s) => ({ value: String(s.id), label: s.name ?? s.code ?? `#${s.id}` }));
  }, [sourcesRaw]);

  const listUrl = useMemo(() => {
    const params = new URLSearchParams({ page: '1', limit: '500' });
    if (search.trim()) params.set('searchParams', search.trim());
    return `/pms-maintenance-counters?${params.toString()}`;
  }, [search]);

  const { data, error, isLoading, mutate } = useSWR(listUrl, fetcher, {
    revalidateOnFocus: true,
  });

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { sortConfig, handleSort } = useGeneralListSort(
    { key: 'name', direction: 'asc' },
    {
      defaultDirectionForKey: (key) =>
        key === 'name' || key === 'vessel' || key === 'type' || key === 'source' || key === 'mode'
          ? 'asc'
          : key === 'currentValue'
            ? 'desc'
            : 'desc',
    },
  );

  useEffect(() => {
    setPage(1);
  }, [search, vesselId, counterTypeId, sourceId, tab, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [sortConfig.key, sortConfig.direction]);

  const baseRows = useMemo(() => {
    const normalized = normalizePaginatedListResponse<PmsMaintenanceCounter>(data, 1, 500);
    return normalized.data.filter((row) => {
      if (vesselId && String(row.vesselId) !== vesselId) return false;
      if (counterTypeId && String(row.counterTypeId) !== counterTypeId) return false;
      if (sourceId && String(row.sourceId) !== sourceId) return false;
      return true;
    });
  }, [data, vesselId, counterTypeId, sourceId]);

  const tabRows = useMemo(() => {
    if (tab === 'all') return baseRows;
    return baseRows.filter((row) => (tab === 'active' ? isActive(row) : !isActive(row)));
  }, [baseRows, tab]);

  const activeCount = useMemo(() => baseRows.filter((row) => isActive(row)).length, [baseRows]);
  const inactiveCount = useMemo(() => baseRows.filter((row) => !isActive(row)).length, [baseRows]);

  const sortedRows = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...tabRows].sort((a, b) => {
      const av = sortValue(a, sortConfig.key);
      const bv = sortValue(b, sortConfig.key);
      if (typeof av === 'number' && typeof bv === 'number') {
        if (av === bv) return 0;
        return av < bv ? -dir : dir;
      }
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [tabRows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  const hasFilters = Boolean(search.trim() || vesselId || counterTypeId || sourceId);

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
    setVesselId('');
    setCounterTypeId('');
    setSourceId('');
  };

  const switchTab = (next: BoardTab) => {
    if (next === tab) return;
    setTab(next);
  };

  const activeTab = useMemo(() => viewTabs.find((t) => t.id === tab) ?? viewTabs[0], [tab]);

  const tabCounts: Record<BoardTab, number | undefined> = {
    active: activeCount,
    inactive: inactiveCount,
    all: baseRows.length,
  };

  const handleDelete = async (e: React.MouseEvent, row: PmsMaintenanceCounter) => {
    e.stopPropagation();
    const label = row.name != null ? String(row.name) : `Counter #${row.id}`;
    const ok = await confirm({
      title: 'Delete counter',
      message: `Delete “${label}”?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await deletePmsMaintenanceCounter(row.id);
      await mutate();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      window.alert(msg || (err instanceof Error ? err.message : 'Failed to delete counter'));
    }
  };

  return (
    <GeneralList
      tabs={viewTabs.map(({ id, label, icon }) => ({
        id,
        label,
        icon,
        count: tabCounts[id],
      }))}
      activeTab={tab}
      onTabChange={(value) => switchTab(value as BoardTab)}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search counters…',
        title: 'Searches counter name and unit',
      }}
      actions={
        <Link
          to="/pms/pms-maintenance-counters/new"
          state={returnState}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
            colors.primary,
          )}
        >
          <PlusIcon className="h-4 w-4" />
          New counter
        </Link>
      }
      filters={
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[150px]">
            <Select
              value={vesselId}
              options={vesselOptions}
              placeholder={vesselsLoading ? 'Loading vessels…' : 'Vessel'}
              showClearButton
              searchPlaceholder="Search vessels…"
              onChange={(e) => setVesselId(String(e.target.value))}
            />
          </div>
          <div className="w-[140px]">
            <Select
              value={counterTypeId}
              options={typeOptions}
              placeholder="Counter type"
              showClearButton
              onChange={(e) => setCounterTypeId(String(e.target.value))}
            />
          </div>
          <div className="w-[140px]">
            <Select
              value={sourceId}
              options={sourceOptions}
              placeholder="Source"
              showClearButton
              onChange={(e) => setSourceId(String(e.target.value))}
            />
          </div>
        </div>
      }
      filtersInline
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={isLoading}
      loadingLabel="Loading counters…"
      isEmpty={!error && pageRows.length === 0}
      emptyState={{
        icon:
          tab === 'active' && !hasFilters ? (
            <DocumentTextIcon className="h-12 w-12" />
          ) : (
            <FolderIcon className="h-12 w-12" />
          ),
        title: hasFilters ? 'No matching counters' : activeTab.emptyTitle,
        hint: hasFilters ? 'Try adjusting filters or clearing the search.' : activeTab.emptyHint,
        action: hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear filters
          </button>
        ) : (
          <Link
            to="/pms/pms-maintenance-counters/new"
            state={returnState}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            New counter
          </Link>
        ),
      }}
      banner={
        error ? (
          <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            Could not load counters.
          </p>
        ) : null
      }
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th className="w-[5.5rem] px-3 py-3 font-medium" />
            <GeneralListSortableTh sortKey="name" className="w-[24%]">
              Counter
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="vessel" className="hidden w-[14%] md:table-cell">
              Vessel
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="type" className="hidden w-[14%] sm:table-cell">
              Type
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="currentValue" className="w-[14%]">
              Value
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="mode" className="hidden w-[12%] lg:table-cell">
              Mode
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="source" className="hidden w-[12%] xl:table-cell">
              Source
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {pageRows.map((row) => {
            const name = row.name != null ? String(row.name) : `Counter #${row.id}`;
            return (
              <tr
                key={row.id}
                onClick={() =>
                  navigate(`/pms/pms-maintenance-counters/${row.id}`, { state: returnState })
                }
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40',
                  colors.border,
                )}
              >
                <td className="px-3 py-3.5">
                  <div className="flex flex-nowrap items-center">
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={`/pms/pms-maintenance-counters/${row.id}`}
                      state={returnState}
                      title="View"
                      aria-label="View counter"
                      className="rounded-lg p-1 text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </Link>
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      to={`/pms/pms-maintenance-counters/${row.id}/edit`}
                      state={returnState}
                      title="Edit"
                      aria-label="Edit counter"
                      className="rounded-lg p-1 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-900"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </Link>
                    <button
                      type="button"
                      title="Delete"
                      aria-label="Delete counter"
                      onClick={(e) => handleDelete(e, row)}
                      className="rounded-lg p-1 text-red-600 transition-colors hover:bg-red-50 hover:text-red-900"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </td>
                <td className="max-w-0 px-3 py-3.5">
                  <div className="min-w-0">
                    <p className={cn('truncate font-medium', colors.text)}>{name}</p>
                    <p className={cn('mt-0.5 truncate text-xs', colors.textMuted)}>
                      {row.unit != null ? String(row.unit) : ''}
                    </p>
                    <p className={cn('mt-1 truncate text-xs md:hidden', colors.textMuted)}>
                      {[vesselLabel(row), typeLabel(row)].filter((v) => Boolean(v)).join(' · ') || ''}
                    </p>
                  </div>
                </td>
                <td
                  className={cn('hidden max-w-0 truncate px-3 py-3.5 md:table-cell', colors.textMuted)}
                  title={vesselLabel(row)}
                >
                  {vesselLabel(row)}
                </td>
                <td
                  className={cn('hidden max-w-0 truncate px-3 py-3.5 sm:table-cell', colors.textMuted)}
                  title={typeLabel(row)}
                >
                  {typeLabel(row)}
                </td>
                <td className={cn('whitespace-nowrap px-3 py-3.5 tabular-nums', colors.text)}>
                  {valueLabel(row)}
                </td>
                <td className="hidden whitespace-nowrap px-3 py-3.5 lg:table-cell">
                  <Badge variant={row.isAutomatic ? 'info' : 'default'} className="whitespace-nowrap">
                    {row.isAutomatic ? 'Automatic' : 'Manual'}
                  </Badge>
                </td>
                <td
                  className={cn('hidden max-w-0 truncate px-3 py-3.5 xl:table-cell', colors.textMuted)}
                  title={sourceLabel(row)}
                >
                  {sourceLabel(row)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};

export default List;
